import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { apiErrorMessage } from '../../api/client'
import { needsApi } from '../../api/endpoints'
import { useAuth } from '../../auth/AuthContext'
import { ErrorMessage } from '../../components/ErrorMessage'
import { OnboardingShell } from '../../components/OnboardingShell'
import type { NeedCategory } from '../../types/api'
import { VISIBLE_LOOKING_FOR } from '../../utils/lookingFor'

/**
 * A tutor opening their learning side.
 *
 * <p>One question, deliberately. Class, board and school were asked here at first and taken back
 * out: they describe a learner rather than start one, they are editable on the profile the moment
 * this finishes, and a form standing between someone and a thing they already decided to do should
 * only ask what it cannot proceed without.
 *
 * <p>"What are you looking for?" earns its place because it is not description - each chip writes a
 * need, and a learner with no needs is invisible to the matcher. Skipping it would create a profile
 * that exists and matches nothing.
 *
 * <p>The chips come from the same list the sign-up form uses, so a category hidden there is hidden
 * here without anyone remembering to do it twice.
 */
export function AddStudentProfilePage() {
  const { profile, addProfile } = useAuth()
  const navigate = useNavigate()

  const [lookingFor, setLookingFor] = useState<Set<NeedCategory>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // The same rules the server enforces, so the offer is not made to someone it would refuse.
  if (profile && (!profile.canAddProfile || profile.persona !== 'SUPPLY')) {
    return <Navigate to="/profile" replace />
  }

  function toggle(category: NeedCategory) {
    setLookingFor((current) => {
      const next = new Set(current)
      if (!next.delete(category)) next.add(category)
      return next
    })
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      // Creates the profile and moves this session onto it - which has to happen first, because
      // a need belongs to the learner profile and there is no learner profile until now.
      await addProfile({})

      let needsSaved = true
      for (const category of lookingFor) {
        await needsApi.create({ category, isPrimary: lookingFor.size === 1 }).catch((err) => {
          console.error(`Need "${category}" could not be saved after creating the profile`, err)
          needsSaved = false
        })
      }

      // A need that failed is not a reason to undo an account that succeeded - the profile page
      // is where needs are managed anyway, so it can say so and let them finish there.
      navigate('/profile', {
        replace: true,
        state: needsSaved
          ? undefined
          : { notice: 'Your learner profile was created, but what you are looking for could not be saved. Please add it below.' },
      })
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create your learner profile'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <OnboardingShell
      title="Create your Learner Profile"
      showcase={false}
    >
      <form onSubmit={onSubmit} className="space-y-5">
        <p className="text-sm text-ink-600">
          Your name, contact details and date of birth carry over - we will not ask again. Just tell
          us what you want to learn, and you can add your class and school on your profile after.
        </p>

        <div>
          <span className="label">What are you looking for?</span>
          <div className="flex flex-wrap gap-2">
            {VISIBLE_LOOKING_FOR.map((opt) => (
              <button
                key={opt.category}
                type="button"
                onClick={() => toggle(opt.category)}
                className={`chip ${lookingFor.has(opt.category) ? 'chip-active' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <span className="mt-2 block text-xs text-ink-400">
            Pick as many as you like, or none - you can change this any time on your profile.
          </span>
        </div>

        <div className="rounded-xl border border-ink-200 bg-ink-50 p-3 text-xs text-ink-600">
          Your two profiles stay separate. Nobody browsing as a learner sees your teaching profile,
          and your reviews and verification stay with the side that earned them.
        </div>

        <ErrorMessage message={error} />

        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate(-1)} className="btn-ghost">
            Back
          </button>
          <button type="submit" disabled={submitting} className="btn-sun flex-1 disabled:opacity-50">
            {submitting ? 'Creating...' : 'Create my learner profile'}
          </button>
        </div>
      </form>
    </OnboardingShell>
  )
}
